import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Mail, AlertTriangle, RotateCcw } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/utils/invokeEdgeFunction';
import { CANDIDATE_STATUS } from '@/types/enums';
import { Sentry, isSentryEnabled } from '@/sentry';
import type { Tables } from '@/integrations/supabase/types';

type Candidate = Tables<'candidates'>;

interface BulkAssessmentSenderProps {
  candidates: Candidate[];
  onUpdate: () => void;
  disabled?: boolean;
}

export const BulkAssessmentSender = React.memo(({
  candidates = [],
  onUpdate,
  disabled = false,
}: BulkAssessmentSenderProps) => {
  const { toast } = useToast();
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [mode, setMode] = useState<'send' | 'resume'>('send');

  const handleConfirm = (nextMode: 'send' | 'resume') => {
    if (candidates.length === 0) {
      toast({
        title: 'No Candidates Selected',
        description: 'Please select candidates before sending assessment links.',
        variant: 'destructive',
      });
      return;
    }

    setMode(nextMode);
    setIsAlertOpen(true);
  };

  const sendAssessmentLinks = async () => {
    setIsSending(true);
    let success = 0;
    let failure = 0;

    for (const candidate of candidates) {
      try {
        let token: string;

        if (mode === 'resume') {
          const result = await invokeEdgeFunction<{ token: string }>(
            'reset-candidate-assessment',
            {
              body: {
                candidateId: candidate.id,
                preserveProgress: true,
              },
            }
          );

          token = result.token;
        } else {
          token = candidate.access_token;
          const now = new Date();
          const tokenExpired =
            !candidate.token_expires_at || new Date(candidate.token_expires_at) <= now;

          if (!token || tokenExpired) {
            token = crypto.randomUUID();
            const expiresAt = new Date();
            expiresAt.setDate(expiresAt.getDate() + 7);
            const { error } = await supabase
              .from('candidates')
              .update({
                access_token: token,
                token_expires_at: expiresAt.toISOString(),
                status: CANDIDATE_STATUS.ASSESSMENT_SENT,
              })
              .eq('id', candidate.id);
            if (error) throw error;
          } else {
            await supabase
              .from('candidates')
              .update({ status: CANDIDATE_STATUS.ASSESSMENT_SENT })
              .eq('id', candidate.id);
          }
        }

        await invokeEdgeFunction(
          'send-assessment-email',
          {
            body: { candidateId: candidate.id, token },
          }
        );
        success++;
      } catch (err) {
        console.error('Failed to send assessment link', err);
        if (isSentryEnabled) Sentry.captureException(err);
        failure++;
      }
    }

    toast({
      title: mode === 'resume' ? 'Bulk Resume & Resend' : 'Bulk Assessment',
      description: `${
        mode === 'resume' ? 'Prepared resume links and resent' : 'Sent'
      } emails to ${success} candidate(s).${
        failure > 0 ? ` ${failure} failed.` : ''
      }`,
      variant: failure > 0 ? 'destructive' : 'default',
    });

    setIsSending(false);
    setIsAlertOpen(false);
    onUpdate();
  };

  const isResumeMode = mode === 'resume';

  return (
    <>
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => handleConfirm('send')}
          disabled={disabled || isSending || candidates.length === 0}
        >
          <Mail className="h-4 w-4 mr-2" />
          {isSending && !isResumeMode
            ? 'Sending...'
            : `Send Assessment (${candidates.length})`}
        </Button>

        <Button
          variant="outline"
          onClick={() => handleConfirm('resume')}
          disabled={disabled || isSending || candidates.length === 0}
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          {isSending && isResumeMode
            ? 'Preparing...'
            : `Resume Assessment (${candidates.length})`}
        </Button>
      </div>

      <AlertDialog open={isAlertOpen} onOpenChange={setIsAlertOpen}>
        <AlertDialogContent className="max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>
              <AlertTriangle className="inline-block mr-2 h-5 w-5 text-yellow-500" />
              {isResumeMode ? 'Confirm Bulk Resume' : 'Confirm Bulk Send'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isResumeMode
                ? `Are you sure you want to resend assessment links for ${candidates.length} selected candidate${candidates.length !== 1 ? 's' : ''} so they can continue from their saved progress? This keeps their answers, keeps their place, and starts them with a fresh timer.`
                : `Are you sure you want to send assessment links to ${candidates.length} selected candidate${candidates.length !== 1 ? 's' : ''}? This action will generate new tokens if necessary and email each candidate.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSending}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={sendAssessmentLinks} disabled={isSending}>
              {isSending
                ? isResumeMode
                  ? 'Preparing...'
                  : 'Sending...'
                : isResumeMode
                  ? 'Confirm Resume & Resend'
                  : 'Confirm & Send'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
});

BulkAssessmentSender.displayName = 'BulkAssessmentSender';
